import * as React from "react";

import CssBaseline from "@material-ui/core/CssBaseline";
import { setLogger, useIsFetching, useIsMutating } from "react-query";
import { Provider as StoreProvider } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";

import { OverlayLoader } from "client/components/OverlayLoader/OverlayLoader";
import { ScrollToTop } from "client/components/ScrollToTop/ScrollToTop";
import { FlagsProvider } from "client/hooks/useFeatureFlags";
import { store as defaultStore } from "client/store";
import { BASE_ROUTE } from "client/utils";

import { BrandingProvider } from "./branding-context";
import { ContactUsProvider } from "./contact-us-context";
import { QueryClientProvider } from "./query-client";
import { RouteProvider } from "./router-context";
import { MopThemeProvider } from "./theme-context";

// RQ sentry logger
setLogger({
  log: console.log,
  warn: console.warn,
  error: (e) => {
    // check if jest is running, if it is then don't log the error
    if (typeof jest !== "undefined") {
      return;
    }
    console.error("Query error: ", e);
  },
});

const LoadingProvider: React.FC = ({ children }) => {
  const isFetching = useIsFetching({
    predicate: ({ queryKey }) =>
      !queryKey.includes("has-self-service-activated"), // remove global loading spinner while polling for instore card transaction on status page
  });
  const isMutating = useIsMutating();
  const showLoading = Boolean(isFetching || isMutating);
  return (
    <>
      {showLoading ? <OverlayLoader /> : null}
      {children}
    </>
  );
};

export const AppProviders: React.FC<{ reduxStore?: any }> = ({
  children,
  reduxStore = {},
}) => {
  const store = { ...defaultStore, ...reduxStore };
  return (
    <QueryClientProvider>
      <StoreProvider store={store}>
        <FlagsProvider>
          <Router basename={BASE_ROUTE}>
            <RouteProvider>
              <BrandingProvider>
                <MopThemeProvider>
                  <CssBaseline />
                  <ScrollToTop />
                  <ContactUsProvider>
                    <LoadingProvider>{children}</LoadingProvider>
                  </ContactUsProvider>
                </MopThemeProvider>
              </BrandingProvider>
            </RouteProvider>
          </Router>
        </FlagsProvider>
      </StoreProvider>
    </QueryClientProvider>
  );
};

export * from "./branding-context";
export * from "./query-client";
export * from "./onboarding-status-context";
